import { useState, useMemo, useEffect } from "react";
import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useFirestoreCollection } from "@/hooks/useFirestore";
import { Avatar } from "@/components/ui/Avatar";
import { Link } from "react-router-dom";
import { useTranslations } from "@/contexts/I18nContext";
import { choreCategoryMeta as categoryMeta } from "@/lib/choreCategory";
import {
  isDoneNow,
  markChoreComplete,
  undoChoreCompletion,
} from "@/lib/chores";
import type { AssignedChore, MemberProfile, HouseholdChore } from "@/lib/models";

/* ------------------------------------------------------------------ */
/*  Status helpers                                                     */
/* ------------------------------------------------------------------ */

type ChoreStatus = "todo" | "in_progress" | "done";

function choreStatus(chore: AssignedChore): ChoreStatus {
  if (isDoneNow(chore)) return "done";
  if (chore.status === "in_progress") return "in_progress";
  return "todo";
}

const STATUS_META: Record<ChoreStatus, { labelKey: string; dot: string }> = {
  todo: { labelKey: "choreboard.status.todo", dot: "bg-red-500" },
  in_progress: { labelKey: "choreboard.status.inProgress", dot: "bg-blue-500" },
  done: { labelKey: "choreboard.status.done", dot: "bg-green-500" },
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export function ChoreboardPage() {
  const { householdId, members, profile, loading: ctxLoading } = useHouseholdContext();
  const t = useTranslations();

  // "everyone" = null, otherwise a specific member uid
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);

  // Authed members (those who have an authUid)
  const authedMembers = useMemo(
    () => members.filter((m) => m.authUid),
    [members],
  );

  // Household chores (for enrichment — points, category, assignees)
  const { data: householdChores } = useFirestoreCollection<HouseholdChore>(
    householdId ? `households/${householdId}/chores` : null,
  );

  const choreMap = useMemo(() => {
    const map: Record<string, HouseholdChore> = {};
    for (const c of householdChores) {
      if (c.uid) map[c.uid] = c;
    }
    return map;
  }, [householdChores]);

  // Fetch assigned chores per authed member
  return (
    <ChoreboardInner
      authedMembers={authedMembers}
      selectedMemberId={selectedMemberId}
      setSelectedMemberId={setSelectedMemberId}
      householdId={householdId}
      profile={profile}
      members={members}
      choreMap={choreMap}
      ctxLoading={ctxLoading}
    />
  );
}

/* We need a separate inner component so we can conditionally call hooks
   per member (the hook count is fixed because authedMembers is stable). */

interface InnerProps {
  authedMembers: MemberProfile[];
  selectedMemberId: string | null;
  setSelectedMemberId: (id: string | null) => void;
  householdId: string | null;
  profile: MemberProfile | null;
  members: MemberProfile[];
  choreMap: Record<string, HouseholdChore>;
  ctxLoading: boolean;
}

function ChoreboardInner({
  authedMembers,
  selectedMemberId,
  setSelectedMemberId,
  householdId,
  profile,
  members,
  choreMap,
  ctxLoading,
}: InnerProps) {
  const t = useTranslations();
  // We fetch assigned chores for every authed member so "Everyone" works.
  // Each member gets their own listener.
  const memberChoresMap = useMultiMemberChores(authedMembers);

  const memberMap = useMemo(() => {
    const map: Record<string, MemberProfile> = {};
    for (const m of members) {
      if (m.uid) map[m.uid] = m;
    }
    return map;
  }, [members]);

  // Aggregate all assigned chores (tag each with ownerId for filtering)
  const allChores = useMemo(() => {
    const result: (AssignedChore & { ownerId: string })[] = [];
    for (const [memberId, chores] of Object.entries(memberChoresMap)) {
      for (const c of chores) {
        result.push({ ...c, ownerId: memberId });
      }
    }
    return result;
  }, [memberChoresMap]);

  // Filter by selected member
  const filteredChores = useMemo(() => {
    if (!selectedMemberId) return allChores; // everyone
    return allChores.filter((c) => c.ownerId === selectedMemberId);
  }, [allChores, selectedMemberId]);

  // Group by status
  const grouped = useMemo(() => {
    const groups: Record<ChoreStatus, (AssignedChore & { ownerId: string })[]> = {
      todo: [],
      in_progress: [],
      done: [],
    };
    for (const c of filteredChores) {
      groups[choreStatus(c)].push(c);
    }
    return groups;
  }, [filteredChores]);

  // Per-member chore counts (for pills)
  const memberChoreCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [memberId, chores] of Object.entries(memberChoresMap)) {
      counts[memberId] = chores.length;
    }
    return counts;
  }, [memberChoresMap]);

  // Status transition handler
  async function handleStatusChange(
    chore: AssignedChore & { ownerId: string },
    action: "start" | "done" | "undo",
  ) {
    if (!chore.uid || !profile?.uid) return;
    if (action === "start") {
      const ref = doc(db, `member_profiles/${chore.ownerId}/assigned_chores`, chore.uid);
      await updateDoc(ref, { status: "in_progress" });
      return;
    }
    if (action === "done") {
      await markChoreComplete({
        chore,
        assigneeUid: chore.ownerId,
        completerUid: profile.uid,
      });
      return;
    }
    if (action === "undo") {
      await undoChoreCompletion({
        chore,
        assigneeUid: chore.ownerId,
      });
      return;
    }
  }

  if (ctxLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "var(--accent-color, #00C7BE)", borderTopColor: "transparent" }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-4 gap-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("choreboard.title")}</h1>
        <Link
          to="/chores"
          className="text-sm font-medium hover:underline"
          style={{ color: "var(--accent-color)" }}
        >
          {t("choreboard.choresList")}
        </Link>
      </div>

      {/* Person selector */}
      <div className="flex gap-2 overflow-x-auto pb-1 px-1">
        {/* Everyone pill */}
        <button
          onClick={() => setSelectedMemberId(null)}
          className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
            selectedMemberId === null
              ? "border-2 text-white"
              : "border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
          }`}
          style={
            selectedMemberId === null
              ? {
                  borderColor: "var(--accent-color)",
                  backgroundColor: "var(--accent-color)",
                }
              : undefined
          }
        >
          <span>{"\u{1F465}"}</span>
          <span>{t("choreboard.everyone")}</span>
          <span className="ml-0.5 opacity-70">{allChores.length}</span>
        </button>

        {authedMembers.map((m) => (
          <button
            key={m.uid}
            onClick={() => setSelectedMemberId(m.uid ?? null)}
            className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              selectedMemberId === m.uid
                ? "border-2"
                : "border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
            }`}
            style={
              selectedMemberId === m.uid
                ? {
                    borderColor: "var(--accent-color)",
                    color: "var(--accent-color)",
                  }
                : undefined
            }
          >
            <Avatar src={m.profile_url} alt={m.name ?? ""} size={20} />
            <span>{m.nickname ?? m.name ?? t("members.detail.unknownMember")}</span>
            <span className="ml-0.5 opacity-70">{memberChoreCounts[m.uid ?? ""] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Kanban columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1 min-h-0">
        {(["todo", "in_progress", "done"] as ChoreStatus[]).map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            chores={grouped[status]}
            memberMap={memberMap}
            choreMap={choreMap}
            onStatusChange={handleStatusChange}
            currentUserId={profile?.uid ?? null}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Multi-member chores hook                                           */
/* ------------------------------------------------------------------ */

function useMultiMemberChores(authedMembers: MemberProfile[]) {
  // One imperative listener per member, registered in an effect so the
  // count is unbounded (the previous implementation hard-coded 8 hook
  // slots — anything beyond was silently dropped). Mirrors iOS
  // ChoreboardView which iterates over the full household roster.
  const [byMember, setByMember] = useState<Record<string, AssignedChore[]>>({});
  const memberKey = authedMembers
    .map((m) => m.uid)
    .filter((uid): uid is string => Boolean(uid))
    .sort()
    .join(",");

  useEffect(() => {
    const memberIds = authedMembers
      .map((m) => m.uid)
      .filter((uid): uid is string => Boolean(uid));
    // Reset prior state when the roster changes so we don't carry
    // stale rows for members that left.
    setByMember((prev) => {
      const next: Record<string, AssignedChore[]> = {};
      for (const id of memberIds) next[id] = prev[id] ?? [];
      return next;
    });
    const unsubs = memberIds.map((uid) => {
      const ref = collection(db, `member_profiles/${uid}/assigned_chores`);
      return onSnapshot(
        ref,
        (snap) => {
          const docs = snap.docs.map(
            (d) => ({ ...d.data(), uid: d.id }) as AssignedChore,
          );
          setByMember((prev) => ({ ...prev, [uid]: docs }));
        },
        (err) => console.error(`assigned_chores listener for ${uid} failed`, err),
      );
    });
    return () => {
      for (const u of unsubs) u();
    };
    // memberKey collapses the array to a stable string so the effect
    // only re-subscribes when the actual roster changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberKey]);

  return byMember;
}

/* ------------------------------------------------------------------ */
/*  Kanban column                                                      */
/* ------------------------------------------------------------------ */

interface KanbanColumnProps {
  status: ChoreStatus;
  chores: (AssignedChore & { ownerId: string })[];
  memberMap: Record<string, MemberProfile>;
  choreMap: Record<string, HouseholdChore>;
  onStatusChange: (chore: AssignedChore & { ownerId: string }, action: "start" | "done" | "undo") => void;
  currentUserId: string | null;
  t: (key: string, params?: Record<string, string | number>) => string;
}

function KanbanColumn({ status, chores, memberMap, choreMap, onStatusChange, currentUserId, t }: KanbanColumnProps) {
  const meta = STATUS_META[status];
  return (
    <div className="flex flex-col bg-gray-50 dark:bg-white/5 rounded-xl overflow-hidden">
      {/* Column header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700">
        <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
        <span className="text-sm font-semibold">{t(meta.labelKey)}</span>
        <span className="text-xs text-gray-400 ml-auto">{chores.length}</span>
      </div>
      {/* Scrollable card list */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {chores.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-6">{t("choreboard.noChores")}</p>
        )}
        {chores.map((chore) => (
          <ChoreCard
            key={`${chore.ownerId}-${chore.uid}`}
            chore={chore}
            status={status}
            memberMap={memberMap}
            choreMap={choreMap}
            onStatusChange={onStatusChange}
            currentUserId={currentUserId}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Chore card                                                         */
/* ------------------------------------------------------------------ */

interface ChoreCardProps {
  chore: AssignedChore & { ownerId: string };
  status: ChoreStatus;
  memberMap: Record<string, MemberProfile>;
  choreMap: Record<string, HouseholdChore>;
  onStatusChange: (chore: AssignedChore & { ownerId: string }, action: "start" | "done" | "undo") => void;
  currentUserId: string | null;
  t: (key: string, params?: Record<string, string | number>) => string;
}

function ChoreCard({ chore, status, memberMap, choreMap, onStatusChange, currentUserId, t }: ChoreCardProps) {
  // Enrich with household chore data
  const hhChore = chore.root_chore_uid ? choreMap[chore.root_chore_uid] : undefined;
  const category = chore.category ?? hhChore?.category;
  const points = chore.points ?? hhChore?.points ?? 0;
  const assigneeIds = hhChore?.assignees ?? [chore.ownerId];
  const catMeta = categoryMeta(category);

  const isDone = status === "done";
  const isOverdue = !isDone && chore.due_date && chore.due_date.toDate() < new Date();

  // Only allow status changes on own chores
  const canAct = chore.ownerId === currentUserId;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm space-y-2">
      {/* Top row: category badge + title */}
      <div className="flex items-start gap-2">
        <span
          className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center text-sm ${catMeta.color} text-white`}
        >
          {catMeta.icon}
        </span>
        <span
          className={`text-sm font-medium leading-snug flex-1 ${
            isDone ? "line-through text-gray-400" : ""
          }`}
        >
          {chore.title}
        </span>
      </div>

      {/* Assignee avatars + points */}
      <div className="flex items-center gap-2">
        <div className="flex -space-x-1.5">
          {assigneeIds.slice(0, 3).map((id) => {
            const m = memberMap[id];
            return (
              <Avatar
                key={id}
                src={m?.profile_url}
                alt={m?.nickname ?? m?.name ?? ""}
                size={22}
                className="ring-2 ring-white dark:ring-gray-800"
              />
            );
          })}
          {assigneeIds.length > 3 && (
            <span className="w-[22px] h-[22px] rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-[10px] font-bold text-gray-500 dark:text-gray-300 ring-2 ring-white dark:ring-gray-800">
              +{assigneeIds.length - 3}
            </span>
          )}
        </div>

        {points > 0 && (
          <span
            className="ml-auto text-[10px] font-bold text-white px-2 py-0.5 rounded-full"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {points} pts
          </span>
        )}
      </div>

      {/* Due date + action button */}
      <div className="flex items-center justify-between">
        {chore.due_date ? (
          <span className={`text-xs ${isOverdue ? "text-red-500 font-medium" : "text-gray-400"}`}>
            {isOverdue ? t("choreboard.overduePrefix") : ""}
            {chore.due_date.toDate().toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </span>
        ) : (
          <span />
        )}

        {canAct && status === "todo" && (
          <button
            onClick={() => onStatusChange(chore, "start")}
            className="px-3 py-1 rounded-md text-xs font-medium text-white transition-colors"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {t("choreboard.actions.start")}
          </button>
        )}
        {canAct && status === "in_progress" && (
          <button
            onClick={() => onStatusChange(chore, "done")}
            className="px-3 py-1 rounded-md text-xs font-medium text-white bg-green-500 hover:bg-green-600 transition-colors"
          >
            {t("choreboard.actions.done")}
          </button>
        )}
        {canAct && status === "done" && (
          <button
            onClick={() => onStatusChange(chore, "undo")}
            className="px-3 py-1 rounded-md text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
          >
            {t("choreboard.actions.undo")}
          </button>
        )}
      </div>
    </div>
  );
}
